import { useEffect, useRef, useState } from "react";
import type { CtcaeTerm } from "../types";

export type PdfTarget = {
  document: CtcaeTerm["source"]["document"];
  page: number;
  title: string;
};

type Props = {
  target?: PdfTarget;
  onClose: () => void;
};

export function PdfModal({ target, onClose }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [frameUrl, setFrameUrl] = useState<string>();
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!target) return;
    const previous = document.activeElement as HTMLElement | null;
    document.body.classList.add("pdf-modal-open");
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.classList.remove("pdf-modal-open");
      document.removeEventListener("keydown", onKeyDown);
      previous?.focus();
    };
  }, [target, onClose]);

  useEffect(() => {
    if (!target) {
      setFrameUrl(undefined);
      setLoadError("");
      return;
    }

    let active = true;
    let objectUrl: string | undefined;
    setFrameUrl(undefined);
    setLoadError("");

    fetch(`/${target.document}`)
      .then((response) => {
        if (!response.ok) throw new Error(`PDF request failed with status ${response.status}.`);
        return response.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (!active) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setFrameUrl(`${objectUrl}#page=${target.page}&view=FitH`);
      })
      .catch((reason: Error) => {
        if (active) setLoadError(reason.message);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [target]);

  if (!target) return null;

  return (
    <div className="modal-layer pdf-modal-layer" role="presentation">
      <button className="modal-backdrop" type="button" aria-label="Close source PDF" onClick={onClose} />
      <section className="pdf-modal" role="dialog" aria-modal="true" aria-labelledby="pdf-modal-title">
        <header className="pdf-modal-header">
          <div>
            <span>Source PDF · page {target.page}</span>
            <h2 id="pdf-modal-title">{target.title}</h2>
          </div>
          <button className="icon-button" type="button" ref={closeRef} aria-label="Close PDF" onClick={onClose}>×</button>
        </header>
        <div className="pdf-frame-shell">
          {frameUrl
            ? <iframe className="pdf-frame" src={frameUrl} title={`${target.title}, page ${target.page}`} />
            : (
              <div className={`pdf-frame-state${loadError ? " error" : ""}`} role="status" aria-live="polite">
                {!loadError && <i aria-hidden="true" />}
                <strong>{loadError ? "The PDF preview could not load." : "Loading source PDF…"}</strong>
                {loadError && <small>{loadError}</small>}
              </div>
            )}
        </div>
        <div className="pdf-modal-footer">
          <span>If the preview is unavailable on this device, you can download the source.</span>
          <a href={`/${target.document}`} download>Download PDF</a>
        </div>
      </section>
    </div>
  );
}
